import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, Rocket, Check } from 'lucide-react'
import { CreateInterventionRequest, InterventionType, IDPItemStatus } from '@/services/api/idpService'

import { IDPIntervention } from '@/services/api/idpService'

interface AddInterventionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CreateInterventionRequest) => Promise<void>
  idpId: string
  editingIntervention?: IDPIntervention | null
}

// #6/#7: enablement types are user-facing as "Enablement type" with the four
// HR slide 10 options. One Growth Enablement Objective can carry several.
const ENABLEMENT_TYPE_OPTIONS: Array<{ value: InterventionType; label: string }> = [
  { value: InterventionType.TRAINING, label: 'Training' },
  { value: InterventionType.WORK_ASSIGNMENT, label: 'Work Assignment' },
  { value: InterventionType.COACHING, label: 'Coaching' },
  { value: InterventionType.EDUCATION, label: 'Education' },
]

export function AddInterventionDialog({
  open,
  onOpenChange,
  onSubmit,
  editingIntervention,
}: AddInterventionDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<CreateInterventionRequest>({
    title: '',
    description: '',
    enablement_types: [InterventionType.TRAINING],
    addresses_development_area_ids: [],
    time_frame: '',
    target_completion_date: '',
    item_status: IDPItemStatus.EMPLOYEE_PROPOSED,
  })

  // Derive the full enablement-type set when editing. Prefer the multi-set
  // (enablement_types); fall back to the legacy scalar for older rows.
  const resolveTypes = (iv: IDPIntervention): InterventionType[] => {
    if (iv.enablement_types && iv.enablement_types.length > 0) return iv.enablement_types
    return iv.intervention_type ? [iv.intervention_type] : []
  }

  // Load data when editing
  useEffect(() => {
    if (editingIntervention) {
      // Extract date part from ISO datetime (e.g., "2025-11-30T00:00:00" -> "2025-11-30")
      const dateValue = editingIntervention.target_completion_date
        ? editingIntervention.target_completion_date.split('T')[0]
        : '';

      setFormData({
        title: editingIntervention.title,
        description: editingIntervention.description || '',
        enablement_types: resolveTypes(editingIntervention),
        addresses_development_area_ids: editingIntervention.addresses_development_area_ids || [],
        time_frame: editingIntervention.time_frame || '',
        target_completion_date: dateValue,
        item_status: editingIntervention.item_status,
      })
    } else {
      setFormData({
        title: '',
        description: '',
        enablement_types: [InterventionType.TRAINING],
        addresses_development_area_ids: [],
        time_frame: '',
        target_completion_date: '',
        item_status: IDPItemStatus.EMPLOYEE_PROPOSED,
      })
    }
  }, [editingIntervention, open])

  const toggleType = (type: InterventionType) => {
    setFormData((prev) => {
      const current = prev.enablement_types || []
      const next = current.includes(type)
        ? current.filter((t) => t !== type)
        : [...current, type]
      return { ...prev, enablement_types: next }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim() || !formData.description?.trim()) return
    if (!(formData.enablement_types && formData.enablement_types.length > 0)) return

    setIsSubmitting(true)
    try {
      await onSubmit(formData)
      // Reset form
      setFormData({
        title: '',
        description: '',
        enablement_types: [InterventionType.TRAINING],
        addresses_development_area_ids: [],
        time_frame: '',
        target_completion_date: '',
        item_status: IDPItemStatus.EMPLOYEE_PROPOSED,
      })
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to add growth enablement:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedTypes = formData.enablement_types || []
  const hasType = selectedTypes.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-green-500" />
            {editingIntervention ? 'Edit Growth Enablement Objective' : 'Add Growth Enablement Objective'}
          </DialogTitle>
          <DialogDescription>
            Plan a specific action or activity to enable your growth.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Complete Project Management Course"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>
              Enablement type <span className="text-red-500">*</span>
            </Label>
            <p className="text-xs text-gray-500">
              Pick all that apply — one objective can combine several.
            </p>
            <div className="flex flex-wrap gap-2">
              {ENABLEMENT_TYPE_OPTIONS.map((opt) => {
                const active = selectedTypes.includes(opt.value)
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleType(opt.value)}
                    aria-pressed={active}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      active
                        ? 'border-emerald-400 bg-emerald-50 text-emerald-800'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {active && <Check className="h-3.5 w-3.5" />}
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this growth enablement involves..."
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="time_frame">Time Frame</Label>
              <Input
                id="time_frame"
                value={formData.time_frame}
                onChange={e => setFormData({ ...formData, time_frame: e.target.value })}
                placeholder="e.g., Q1 2025"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="target_completion_date">Target Date</Label>
              <Input
                id="target_completion_date"
                type="date"
                value={formData.target_completion_date}
                onChange={e => setFormData({ ...formData, target_completion_date: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !formData.title.trim() || !hasType}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingIntervention ? 'Update Growth Enablement' : 'Add Growth Enablement'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
